import {
  buildArchiveWithParentUpdate,
  buildGoalLinkWrites,
  buildGoalParentLinkWrites,
  buildTaskUpdatesWithLinks,
} from '@griply/services';
import { GoalType } from '@griply/types';
import {
  getGoalParentIds,
  isResolvedColor,
  resolveFrozenGoalColor,
  resolveLinkSeedColor,
} from '@griply/utils';
import { useMetricTemplates } from 'features/app-config';
import { useCreateGoal, useGoals, useUpdateGoal } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import { useUpdateTasks } from 'features/tasks';
import { useIsFavorite, useToggleFavorite } from 'features/user';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useToday } from 'shared/contexts/today';
import { useUpdateGoalTaskCountMutation } from 'shared/hooks/queries/use-update-goal-task-count-mutation';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { useModifierKeysDown } from 'shared/hooks/use-modifier-keys-down';
import { usePremiumFeatureAllowed } from 'shared/hooks/use-premium-feature-allowed';
import { useUpdateRelationshipsReferences } from 'shared/hooks/use-update-relationships-references';
import { useUser } from 'shared/hooks/use-user';
import { useI18nLocale } from 'shared/lib/react-i18next/use-i18n-locale';
import { initialDateFormat } from 'shared/types/date-format-options';
import { Goal, NewGoal } from 'shared/types/goal';
import { GoalFormFields } from 'shared/types/goal-form';
import { ID } from 'shared/types/id';
import { LimitKeys } from 'shared/types/limit-keys';
import { Task } from 'shared/types/task';
import { FavoriteType } from 'shared/types/user-settings';
import { getGoalDetailUrl } from 'shared/utils/get-goal-detail-url';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { targetIsInput } from 'shared/utils/target-is-input';

import {
  GoalFormContext,
  GoalFormContextProps,
  GoalFormSeed,
} from './goal-form-context';
import { mapGoalFieldsToGoal } from './map-goal-fields-to-goal';
import { mapGoalToGoalFields } from './map-goal-to-goal-fields';
import { resolveFormLifeAreaIds } from './resolve-form-life-area-ids';

const loadGoalFormDialog = () => import('shared/dialogs/goal-form');
const GoalFormDialog = React.lazy(async () => ({
  default: (await loadGoalFormDialog()).GoalFormDialog,
}));

export type GoalFormProviderProps = {
  children: React.ReactNode;
};

export const GoalFormProvider: React.FC<GoalFormProviderProps> = ({
  children,
}) => {
  const locale = useI18nLocale();
  const navigate = useNavigate();
  const user = useUser();
  const today = useToday();
  const lifeAreas = useLocalizedLifeAreas();
  const goals = useGoals();
  const metricTemplates = useMetricTemplates();

  // useOpenPremiumDialog is part of a higher level context which should be kept in mind (probably different setup needed)
  const openPremiumDialog = useOpenPremiumDialog();
  const { allowed: uploadImageAllowed } = usePremiumFeatureAllowed(
    LimitKeys.GoalUserPhoto,
    {},
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [goal, setGoal] = useState<GoalFormSeed>();
  const [goalId, setGoalId] = useState<ID>();
  const [tasksToLink, setTasksToLink] = useState<Task[]>([]);

  const { submit: updateTasks } = useUpdateTasks();
  const { mutate: updateGoalTaskCount } = useUpdateGoalTaskCountMutation();
  const updateRelationships = useUpdateRelationshipsReferences();
  const links = useEntityLinks();

  // On create there is no document to resolve links from, so the seed carries the picker's values.
  const currentParentIds = useMemo(
    () =>
      goal?.id
        ? getGoalParentIds(goal as Goal, { links })
        : goal?.parentIds ?? [],
    [goal, links],
  );

  const currentLifeAreaIds = useMemo(
    () => resolveFormLifeAreaIds(goal, links),
    [goal, links],
  );
  const [skipNavigation, setSkipNavigation] = useState(false);
  const create = useCreateGoal();
  const update = useUpdateGoal();
  const { retry, isLoading, error, isSuccess, reset } = useMemo(
    () => (goalId ? update : create),
    [create, goalId, update],
  );
  const isFavorite = useIsFavorite(goalId);
  const toggleFavorite = useToggleFavorite();

  const filteredLifeAreas = useMemo(
    () => lifeAreas.filter(({ archivedAt }) => !archivedAt),
    [lifeAreas],
  );

  const openDialog = useCallback(
    (goal?: GoalFormSeed, id?: ID, skipParams?: boolean, tasks?: Task[]) => {
      setGoalId(id);
      setGoal(
        id ? goal : { ...(goal ?? {}), startDate: goal?.startDate ?? today },
      );
      setSkipNavigation(!!skipParams);
      setTasksToLink(tasks ?? []);
      setDialogOpen(true);
    },
    [today],
  );

  const closeDialog = useCallback(() => {
    reset();
    setGoal(undefined);
    setGoalId(undefined);
    setSkipNavigation(false);
    setDialogOpen(false);
  }, [reset]);

  const submitForm = useCallback(
    async (values: GoalFormFields) => {
      const processedValues = mapGoalFieldsToGoal(values, {
        metricTemplates,
        metricEntries: goal?.metric?.entries ?? [],
      });

      if (!processedValues) {
        return;
      }

      if (goalId) {
        // Not routed through mergeGoalLinkChanges: that resolves the freeze from the stored goal
        // alone, which would overwrite the colour this form is submitting.
        const frozenColor = isResolvedColor(values.color)
          ? undefined
          : resolveFrozenGoalColor(
              { id: goalId, color: goal?.color },
              { goals, lifeAreas, links },
            );

        const linkWrites = buildGoalLinkWrites({
          goalId,
          lifeAreaIds: values.lifeAreaIds ?? [],
          currentLifeAreaIds,
          isMigrated: !!goal?.lifeAreaLinksMigrated,
          frozenColor,
        });

        // An absent field means the form never carried parents, not "remove them all".
        const parentWrites = values.parentIds
          ? buildGoalParentLinkWrites({
              goalId,
              parentIds: values.parentIds,
              currentParentIds,
              isMigrated: !!goal?.parentLinksMigrated,
              frozenColor,
            })
          : { goalUpdate: null, relationshipUpdates: [] };

        const parentIds = values.parentIds ?? [];
        update.submit(
          {
            ...processedValues,
            ...(linkWrites.goalUpdate ?? {}),
            ...(parentWrites.goalUpdate ?? {}),
            id: goalId,
            ...buildArchiveWithParentUpdate({
              parentGoalId: parentIds[parentIds.length - 1],
              goal: { archivedAt: goal?.archivedAt ?? null },
              allGoals: goals,
            }),
          },
          true,
        );

        const relationshipUpdates = [
          ...linkWrites.relationshipUpdates,
          ...parentWrites.relationshipUpdates,
        ];
        relationshipUpdates.length && updateRelationships(relationshipUpdates);
        isFavorite !== values.favorite &&
          toggleFavorite(goalId, FavoriteType.Goal);
        return;
      }

      // Calculate task counts from tasksToLink
      const taskCount = tasksToLink.length;
      const completedTaskCount = tasksToLink.filter(
        (t) => t.completedAt,
      ).length;

      const createdGoal = await create.submit(
        {
          ...processedValues,
          ...(goal?.type ? { type: goal.type } : {}),
          taskCount: processedValues.taskCount ?? taskCount,
          completedTaskCount:
            processedValues.completedTaskCount ?? completedTaskCount,
        } as NewGoal,
        {
          lifeArea: filteredLifeAreas.find(
            ({ id }) => id === values.lifeAreaIds?.[0],
          )!,
          links: {
            parentIds: values.parentIds ?? [],
            lifeAreaIds: values.lifeAreaIds ?? [],
          },
        },
      );

      if (tasksToLink.length) {
        const taskWrites = buildTaskUpdatesWithLinks(
          tasksToLink.map((task) => ({
            task,
            update: { id: task.id, goalIds: [createdGoal.id], lifeAreaIds: [] },
          })),
        );
        updateTasks(taskWrites.taskUpdates);
        taskWrites.relationshipUpdates.length &&
          updateRelationships(taskWrites.relationshipUpdates);
        // The new goal's counts rode its create write; only the goals the tasks leave take a delta.
        taskWrites.goalCountDeltas
          .filter(({ goalId: id }) => id !== createdGoal.id)
          .forEach((delta) => updateGoalTaskCount(delta));
      }

      !!values.favorite && toggleFavorite(createdGoal.id, FavoriteType.Goal);

      if (!skipNavigation && !values.parentIds?.length) {
        navigate(getGoalDetailUrl(createdGoal));
      }
    },
    [
      create,
      filteredLifeAreas,
      goal?.archivedAt,
      goal?.color,
      goal?.metric?.entries,
      goal?.type,
      goalId,
      goals,
      isFavorite,
      lifeAreas,
      links,
      metricTemplates,
      navigate,
      skipNavigation,
      tasksToLink,
      toggleFavorite,
      update,
      currentLifeAreaIds,
      currentParentIds,
      goal?.parentLinksMigrated,
      goal?.lifeAreaLinksMigrated,
      updateRelationships,
      updateGoalTaskCount,
      updateTasks,
    ],
  );

  // The first link's real colour, so a new goal starts coloured without storing a sentinel.
  const linkSeedColor = useMemo(
    () =>
      resolveLinkSeedColor(currentParentIds, currentLifeAreaIds, {
        goals,
        lifeAreas,
        links,
      }),
    [currentParentIds, currentLifeAreaIds, goals, lifeAreas, links],
  );

  const initialVals = useMemo(() => {
    const fieldValues = goal
      ? mapGoalToGoalFields(goal, {
          locale,
          lifeAreaIds: currentLifeAreaIds,
          parentIds: currentParentIds,
          linkSeedColor,
        })
      : {};
    // startDate should be today for a new goal
    const startDate = goalId ? fieldValues?.startDate : today;
    return {
      startDate,
      ...fieldValues,
      favorite: isFavorite,
    };
  }, [
    goal,
    goalId,
    locale,
    isFavorite,
    today,
    currentLifeAreaIds,
    currentParentIds,
    linkSeedColor,
  ]);

  useEffect(() => {
    if (isSuccess) {
      closeDialog();
    }
  }, [closeDialog, isSuccess]);

  const value = useMemo<GoalFormContextProps>(
    () => ({
      onCreateGoal: (options) => {
        const { tasks, skipNavigation, ...initialValues } = options ?? {};
        openDialog(initialValues, undefined, skipNavigation, tasks);
      },
      onEditGoal: (initialValues, id) => openDialog(initialValues, id),
    }),
    [openDialog],
  );

  useEffect(() => {
    // hack around goal form loading, make sure it is loaded for offline use
    loadGoalFormDialog();
  }, []);

  useModifierKeysDown(
    'KeyN',
    (e) => {
      if (targetIsInput(e.target)) {
        return; // Don't do a thing
      }
      openDialog();
      e.preventDefault();
    },
    { command: true, option: true },
  );

  return (
    <GoalFormContext.Provider value={value}>
      {children}
      <Suspense>
        <GoalFormDialog
          initialValues={initialVals}
          metricTemplates={metricTemplates}
          lifeAreas={filteredLifeAreas}
          goals={goals}
          dateFormat={user?.settings?.dateFormat ?? initialDateFormat}
          weekStartsOn={getWeekStartsOn(user)}
          open={dialogOpen}
          onClose={closeDialog}
          onSubmit={submitForm}
          onRetry={retry}
          isLoading={isLoading}
          isError={!!error}
          isEdit={!!goalId}
          goalId={goalId}
          links={links}
          isProject={goal?.type === GoalType.Project}
          isPremiumImageUploadAllowed={uploadImageAllowed}
          onPremium={openPremiumDialog}
        />
      </Suspense>
    </GoalFormContext.Provider>
  );
};
